import { ErrorOutlineRounded, TravelExploreRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import EmailInput from "../components/elements/EmailInput";
import OnboardingLoginAndRegister from "../components/elements/OnboardingLoginAndRegister";
import OTPInput from "../components/elements/OTPInput";
import PassportInput from "../components/elements/PassportInput";
import { AMERICAS_COUNTRIES } from "../config/config";
import { useLogin } from "./hooks/useLogin";
import { useSaveDefaultPlan } from "./hooks/useSaveDefaultPlan";

export default function LoginPage() {
  useSaveDefaultPlan();
  const location = useLocation();
  const sessionExpired =
    new URLSearchParams(location.search).get("session") === "expired";
  const [country, setCountry] = useState(
    AMERICAS_COUNTRIES.find((c) => c.code === "+57")
  );
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidDoc, setIsValidDoc] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    email,
    setEmail,
    passport,
    setPassport,
    code,
    setCode,
    step,
    error,
    isSubmitting,
    isSubmittingEmail,
    isLogged,
    isCodeValid,
    handleSendCodeByEmail,
    handleVerifyCodeByEmail,
    recaptchaContainerRef,
  } = useLogin({ country });

  if (isLogged) window.location.href = "/dashboard";

  const loginForm = (
    <Card
      sx={{
        maxWidth: 420,
        mx: "auto",
        borderRadius: 4,
        background: "#fff",
        boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          {sessionExpired && (
            <Alert severity="warning" sx={{ width: "100%" }}>
              Tu sesión expiró por seguridad. Ingresa nuevamente para continuar.
            </Alert>
          )}

          <Box
            sx={{
              bgcolor: "primary.main",
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 20px rgba(24,59,255,0.3)",
            }}
          >
            <TravelExploreRounded sx={{ color: "#fff", fontSize: 30 }} />
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              background:
                "linear-gradient(90deg, #0E1EFD 0%, #0066FF 50%, #0099FF 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            Inicia tu solicitud de Visa Americana con Convisas
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 1.5, fontWeight: 500 }}
          >
            Accede para realizar tu trámite de forma segura
          </Typography>

          {step === "input" && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                maxWidth: 320,
                lineHeight: 1.4,
                fontSize: "0.85rem",
                opacity: 0.9,
                mt: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid rgba(0, 100, 255, 0.18)",
                backgroundColor: "rgba(0, 60, 180, 0.03)",
                backdropFilter: "blur(2px)",
              }}
            >
              Ingresa tu número de pasaporte y tu correo electrónico para crear
              tu cuenta o iniciar sesión. Recibirás un código de verificación en
              tu correo. Introdúcelo para confirmar tu identidad y acceder.
            </Typography>
          )}
        </Stack>

        {step === "input" && (
          <form onSubmit={handleSendCodeByEmail}>
            <Stack spacing={2} sx={{ mt: 3 }}>
              <PassportInput
                passport={passport}
                setPassport={setPassport}
                setIsValid={setIsValidDoc}
              />
              <EmailInput
                email={email}
                setEmail={setEmail}
                setIsValid={setIsValidEmail}
              />
            </Stack>

            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{
                mt: 3,
                py: 1.3,
                fontWeight: 600,
                borderRadius: 2,
                background: "linear-gradient(90deg, #183BFF 0%, #0061FF 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #0E1EFD 0%, #0066FF 100%)",
                },
              }}
              disabled={!(isValidEmail && isValidDoc)}
              onClick={(e) => {
                e.preventDefault();
                handleSendCodeByEmail(e);
              }}
              startIcon={
                isSubmittingEmail ? <CircularProgress size={20} /> : null
              }
            >
              Enviar código de acceso
            </Button>
          </form>
        )}

        {step === "codeEmail" && (
          <form ref={formRef} onSubmit={handleVerifyCodeByEmail}>
            <Stack spacing={2} sx={{ mt: 3 }}>
              <OTPInput
                length={6}
                value={code}
                onChange={setCode}
                onComplete={() =>
                  setTimeout(() => formRef.current?.requestSubmit(), 50)
                }
              />
              <Button
                fullWidth
                variant="contained"
                type="submit"
                sx={{
                  py: 1.3,
                  fontWeight: 600,
                  borderRadius: 2,
                  background:
                    "linear-gradient(90deg, #0E1EFD 0%, #0066FF 100%)",
                }}
                disabled={!isCodeValid || isSubmittingEmail}
              >
                {isSubmittingEmail ? (
                  <CircularProgress size={22} />
                ) : (
                  "Verificar código"
                )}
              </Button>
            </Stack>
          </form>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              fontWeight: 600,
              fontSize: 15,
              textAlign: "center",
            }}
            iconMapping={{
              error: <ErrorOutlineRounded sx={{ fontSize: 26 }} />,
            }}
          >
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "#f9fbff",
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            position: "relative",
            backgroundImage: "url('/assets/icons/background.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(5,15,60,0.9) 0%, rgba(0,60,130,0.7) 50%, rgba(255,255,255,0.15) 100%)",
              backdropFilter: "blur(3px)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              textAlign: "center",
              mb: 3,
              mt: -4,
            }}
          >
            <img
              src="/assets/icons/logo.png"
              alt="Convisas Logo"
              style={{
                maxWidth: 110,
                borderRadius: 20,
                padding: 8,
                boxShadow: "0 6px 24px rgba(255,255,255,0.2)",
              }}
              draggable={false}
            />
            <Typography
              variant="h6"
              fontWeight={700}
              color="#fff"
              sx={{
                textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                fontFamily: "Montserrat, sans-serif",
                mt: 1,
              }}
            >
              Bienvenido, estás a un paso de comenzar tu solicitud de visa
              americana de turismo con el respaldo de Convisas.
            </Typography>
            <Typography
              variant="body2"
              color="rgba(255,255,255,0.85)"
              sx={{ maxWidth: 300, mx: "auto", lineHeight: 1.5 }}
            >
              Avanza con seguridad y disfruta de un proceso claro, guiado y
              confiable para visitar los Estados Unidos como turista.{" "}
            </Typography>
          </Box>

          <Box sx={{ position: "relative", zIndex: 2, width: "100%", p: 3 }}>
            {loginForm}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #f2f6ff 0%, #ffffff 60%, #dfe9ff 100%)",
              zIndex: 2,
            }}
          >
            {loginForm}
          </Box>
          <OnboardingLoginAndRegister />
        </Box>
      )}
    </Box>
  );
}
